import clsx from 'clsx';
import type { Money, Quantity } from '@prody/shared';
import { useFormat } from '~/platform/tenancy/BusinessProvider';
import './Numeric.css';

/* ------------------------------------------------------------------ *
 * Numbers get their own typeface, their own alignment and their own
 * components. In this domain a screen is mostly measurements; treating
 * them as ordinary text is the difference between a table you can scan
 * and one you have to read.
 *
 * Every component here goes through the tenant's formatters, so a
 * price is never rendered with the browser's locale by accident.
 * ------------------------------------------------------------------ */

interface MoneyTextProps {
  value: Money | null | undefined;
  /** Strike-through the previous value beside it — the chalk board move. */
  previous?: Money | null;
  bare?: boolean;
  signed?: boolean;
  compact?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Colour by sign. Only for balances and deltas, never for a list price. */
  tone?: boolean;
  className?: string;
}

export function MoneyText({
  value,
  previous,
  bare = false,
  signed = false,
  compact = false,
  size = 'md',
  tone = false,
  className,
}: MoneyTextProps) {
  const f = useFormat();
  const sign = value ? Math.sign(value.minor) : 0;
  const changed = previous && value && previous.minor !== value.minor;

  return (
    <span
      className={clsx('num pd-money', className)}
      data-size={size}
      data-sign={tone && sign !== 0 ? (sign > 0 ? 'pos' : 'neg') : undefined}
    >
      {changed ? (
        <span className="chalk-struck pd-money__was">
          {f.money(previous, { bare: true })}
        </span>
      ) : null}
      <span className="pd-money__now">{f.money(value, { bare, signed, compact })}</span>
    </span>
  );
}

/**
 * Quantity with the unit set a step down and dimmed. The number is
 * what you compare down a column; the unit is a reminder.
 */
export function QuantityText({
  value,
  /** Shown struck through when less was picked than ordered. */
  ordered,
  className,
}: {
  value: Quantity | null | undefined;
  ordered?: Quantity | null;
  className?: string;
}) {
  const f = useFormat();
  const short =
    ordered && value && value.unit === ordered.unit && value.value < ordered.value;

  if (!value) return <span className={clsx('num faint', className)}>—</span>;

  return (
    <span className={clsx('num pd-qty', className)} data-short={short || undefined}>
      {short ? (
        <span className="chalk-struck pd-qty__was">{f.quantity(ordered, { bare: true })}</span>
      ) : null}
      <span className="pd-qty__value">{f.quantity(value, { bare: true })}</span>
      <span className="pd-qty__unit">{f.quantity(value).split(' ').at(-1)}</span>
    </span>
  );
}

/**
 * A change against a reference, as a percentage. Renders an arrow
 * glyph rather than a coloured triangle so it survives a black-and-
 * white warehouse printout.
 */
export function Delta({
  bps,
  invert = false,
  className,
}: {
  bps: number | null;
  /** For cost columns, where up is bad. */
  invert?: boolean;
  className?: string;
}) {
  const f = useFormat();
  if (bps === null || bps === 0) {
    return <span className={clsx('num faint', className)}>·</span>;
  }
  const up = bps > 0;
  const good = invert ? !up : up;
  return (
    <span
      className={clsx('num pd-delta', className)}
      data-tone={good ? 'ok' : 'alert'}
    >
      <span aria-hidden="true">{up ? '▲' : '▼'}</span>
      {f.percentFromBps(Math.abs(bps))}
    </span>
  );
}

/**
 * A fill-rate / progress bar drawn as a segmented gauge — the notches
 * on the side of a measuring jug, not a rounded pill.
 */
export function Gauge({
  value,
  label,
  tone = 'accent',
  className,
}: {
  /** 0..1 */
  value: number;
  label?: string;
  tone?: 'accent' | 'ok' | 'warn' | 'alert';
  className?: string;
}) {
  const pct = Math.max(0, Math.min(1, value));
  return (
    <span
      className={clsx('pd-gauge', className)}
      data-tone={tone}
      role="meter"
      aria-valuenow={Math.round(pct * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <span className="pd-gauge__track">
        <span className="pd-gauge__fill" style={{ scale: `${pct} 1` }} />
      </span>
      <span className="num pd-gauge__value">{Math.round(pct * 100)}%</span>
    </span>
  );
}
